import { useState, useCallback, useEffect, useRef } from 'react'
import { Check, ChevronsUpDown, Loader2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'
import { trpc } from '@/lib/api/trpc'
import { Button } from '@/components/ui/button'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/components/ui/command'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover'

interface TaxCategory {
	id: string
	slug: string
	label: string
	taxjarCode: string | null
}

interface TaxCategorySelectProps {
	value: string | null | undefined
	onChange: (value: string | null) => void
	selectedTaxCategory?: TaxCategory | null
	placeholder?: string
	disabled?: boolean
	className?: string
}

const DEBOUNCE_MS = 300
const MIN_SEARCH_LENGTH = 1

/** Debounced search input wired to taxCategories.search tRPC query. */
export function TaxCategorySelect({
	value,
	onChange,
	selectedTaxCategory,
	placeholder,
	disabled = false,
	className,
}: TaxCategorySelectProps) {
	const { t } = useTranslation('common')
	const resolvedPlaceholder = placeholder ?? t('taxCategorySelect.searchPlaceholder')
	const [open, setOpen] = useState(false)
	const [search, setSearch] = useState('')
	const [debouncedSearch, setDebouncedSearch] = useState('')
	const [displayLabel, setDisplayLabel] = useState(
		selectedTaxCategory?.label ?? '',
	)
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const needsResolve = !!value && !displayLabel && !selectedTaxCategory
	const { data: resolved, isLoading: isResolving } = trpc.taxCategories.getById.useQuery(
		{ id: value! },
		{ enabled: needsResolve },
	)

	useEffect(() => {
		if (selectedTaxCategory) {
			setDisplayLabel(selectedTaxCategory.label)
		} else if (resolved) {
			setDisplayLabel(resolved.label)
		}
	}, [selectedTaxCategory, resolved])

	const scheduleDebounce = useCallback((term: string) => {
		if (timerRef.current) clearTimeout(timerRef.current)
		timerRef.current = setTimeout(() => setDebouncedSearch(term), DEBOUNCE_MS)
	}, [])

	const handleSearchChange = useCallback(
		(term: string) => {
			setSearch(term)
			scheduleDebounce(term)
		},
		[scheduleDebounce],
	)

	const shouldSearch = debouncedSearch.length >= MIN_SEARCH_LENGTH

	const { data: results, isLoading } = trpc.taxCategories.search.useQuery(
		{ searchValue: debouncedSearch },
		{ enabled: open && shouldSearch },
	)

	const handleSelect = useCallback(
		(category: TaxCategory) => {
			onChange(category.id)
			setDisplayLabel(category.label)
			setOpen(false)
			setSearch('')
			setDebouncedSearch('')
		},
		[onChange],
	)

	const handleClear = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation()
			onChange(null)
			setDisplayLabel('')
		},
		[onChange],
	)

	const handleOpenChange = useCallback((nextOpen: boolean) => {
		setOpen(nextOpen)
		if (!nextOpen) {
			setSearch('')
			setDebouncedSearch('')
		}
	}, [])

	return (
		<Popover open={open} onOpenChange={handleOpenChange}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					role="combobox"
					aria-expanded={open}
					disabled={disabled}
					className={cn('h-9 w-full justify-between font-normal', className)}
				>
					{needsResolve && isResolving ? (
						<span className="flex items-center gap-2 text-muted-foreground">
							<Loader2 className="size-3.5 animate-spin" />
							{t('loading')}
						</span>
					) : value && displayLabel ? (
						<span className="truncate">{displayLabel}</span>
					) : (
						<span className="text-muted-foreground">{resolvedPlaceholder}</span>
					)}
					<div className="ml-2 flex shrink-0 items-center gap-1">
						{value && !disabled && (
							<span
								role="button"
								tabIndex={0}
								onClick={handleClear}
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault()
										handleClear(e as unknown as React.MouseEvent)
									}
								}}
								aria-label={t('taxCategorySelect.clearSelection')}
								className="rounded-sm opacity-70 transition-opacity hover:opacity-100"
							>
								<X className="size-3.5" />
							</span>
						)}
						<ChevronsUpDown className="size-4 shrink-0 opacity-50" />
					</div>
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
				<Command shouldFilter={false}>
					<CommandInput
						placeholder={t('taxCategorySelect.searchPlaceholder')}
						value={search}
						onValueChange={handleSearchChange}
					/>
					<CommandList>
						<CommandEmpty>
							{isLoading
								? t('taxCategorySelect.searching')
								: !shouldSearch
									? t('taxCategorySelect.typeToSearch')
									: t('taxCategorySelect.noResults')}
						</CommandEmpty>
						{results && results.length > 0 && (
							<CommandGroup>
								{results.map((category) => (
									<CommandItem
										key={category.id}
										value={category.id}
										onSelect={() => handleSelect(category as TaxCategory)}
									>
										<Check
											className={cn(
												'mr-2 size-4 shrink-0',
												value === category.id
													? 'opacity-100'
													: 'opacity-0',
											)}
										/>
										<div className="flex min-w-0 flex-1 flex-col">
											<span className="truncate text-sm">
												{category.label}
											</span>
											<span className="text-muted-foreground truncate text-xs">
												{category.slug}
												{category.taxjarCode && ` — ${category.taxjarCode}`}
											</span>
										</div>
									</CommandItem>
								))}
							</CommandGroup>
						)}
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	)
}
